import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../stores/root';

// assets
import { faCircleCheck, faCircleExclamation, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import GeneralService from '../../../../../services/general';
import OrderTruckService from '../../../../../services/orders/trucks';

// props
import { IOrderContainerResourceShortProps } from '../../../../../props/orders/containers';
import { IOrderTruckResourceShortProps } from '../../../../../props/orders/trucks';

// components
import { CommandBar, IColumn, ICommandBarItemProps, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../typography/text';
import Tag from '../../../../uiframeworks/tag';
import Label from '../../../../typography/label';
import OrderTruckForm from '../form';
import OrderTruckDetails from '../details';
import PermissionsService from '../../../../../services/permissions';

interface IOrderTrucksListProps {
    orderId: string;
    qs?: string[];
    trucks?: IOrderTruckResourceShortProps[];
    columns?: TOrderContainerColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TOrderContainerColumn = 'name' | 'goodsList' | 'address' | 'price';

const OrderTrucksList: React.FC<IOrderTrucksListProps> = (props: IOrderTrucksListProps) => {
    const shownColumns: TOrderContainerColumn[] = props.columns || [
        'name',
        'address',
        'goodsList',
        'price'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [trucks, setTrucks] = React.useState<IOrderTruckResourceShortProps[]>(props.trucks || []);
    const [activeTruck, setActiveTruck] = React.useState<IOrderTruckResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            name: "Name / Armada",
            minWidth: 190,
            maxWidth: 220,
            isMultiline: true,
            onRender: (item: IOrderTruckResourceShortProps) => {
                const hasGoods = item.goods && item.goods.length > 0;
                const nopol = item.vehicleRegistrationNumber || item.truck?.registrationNumber;
                const driver = item.driverName;

                return <Stack styles={{ root: { padding: '4px 0px' } }} tokens={{ childrenGap: 3 }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Link onClick={() => {
                            setActiveSurface('details');
                            setActiveTruck(item);
                        }}><Text size={'small'}>{item.name || "-"}</Text></Link>

                        {hasGoods ? (
                            <Stack horizontal tokens={{ childrenGap: 6 }}>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: '#15803d',
                                    backgroundColor: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '12px',
                                    padding: '1px 7px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />
                                    Terisi ({item.goods.length})
                                </span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: '#0369a1',
                                    backgroundColor: '#f0f9ff',
                                    border: '1px solid #bae6fd',
                                    borderRadius: '12px',
                                    padding: '1px 7px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Qty ({GeneralService.getNumberWithSeparator(item.goods.reduce((acc, curr) => acc + (parseFloat(curr.quantityAmount as any) || 0), 0))})
                                </span>
                            </Stack>
                        ) : (
                            <span style={{
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#b91c1c',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '12px',
                                padding: '1px 7px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                            }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }} />
                                Belum Diisi
                            </span>
                        )}
                    </Stack>

                    <div>
                        {nopol ? (
                            <span 
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    backgroundColor: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    width: 'fit-content',
                                    whiteSpace: 'nowrap'
                                }}
                                title={driver ? `Sopir: ${driver}` : 'Nomor Polisi Truk'}
                            >
                                <span>{nopol}</span>
                                {driver && <span style={{ color: '#64748b', fontWeight: 400, fontSize: '10px' }}>({driver})</span>}
                            </span>
                        ) : (
                            <span 
                                style={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    backgroundColor: '#f8fafc',
                                    color: '#94a3b8',
                                    border: '1px dashed #cbd5e1',
                                    borderRadius: '4px',
                                    padding: '1px 5px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    width: 'fit-content',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <span>Nopol belum diisi</span>
                            </span>
                        )}
                    </div>
                </Stack>
            }
        },
        {
            key: "goodsList",
            name: "Goods List",
            minWidth: 250,
            onRender: (item: IOrderTruckResourceShortProps) => {
                if (item.goods.length <= 0) {
                    return <Stack styles={{ root: { padding: '4px 0px' } }}>
                        <Text size={'small'} style={{ color: '#94a3b8', fontStyle: 'italic' }}>Good(s) are not listed yet</Text>
                    </Stack>
                } else {
                    return <Stack wrap horizontal tokens={{ childrenGap: 10 }}>
                        {item.goods.map((g) => {
                            return <Tag key={g.id || g.goods?.id}>
                                <Stack>
                                    <Label size={'xsmall'}>{g.goods.name}</Label>
                                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(g.quantityAmount)} {g.goods.quantityUnit}</Text>
                                </Stack>
                            </Tag>
                        })}
                    </Stack>
                }
            }
        },
        {
            key: "price",
            name: "Price (inc. PPn & PPh)",
            minWidth: 175,
            maxWidth: 175,
            onRender: (item: IOrderTruckResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(item.totalPrice)}</Text>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.trucks) {
            _onRetrieveTrucks();
        } else {
            setLoaded(true);
        }
    }, []);

    const _onRetrieveTrucks = async (pageNumber?: number,) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = props.qs || [];
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const results = await OrderTruckService.retrieve(props.orderId, qs.join("&"));
            setTrucks(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_trucks_list_error',
                text: 'Failed to retrieve order trucks list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                }
            });
        }

        if (!props.hideCreateButton && PermissionsService.hasPermission(['orders.update.all'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create Truck",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Truck"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveTruck(undefined);

        if (refresh) { _onRetrieveTrucks() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        <Stack className={styles[props.variant || 'card']}>
            {
                !loaded || (loaded && trucks.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={trucks.filter((truck) => {
                                const search = (keyword || "").trim().toLowerCase();
                                return (truck.truck?.registrationNumber || "").toLowerCase().indexOf(search) > -1 || (truck.truck?.name || "").toLowerCase().indexOf(search) > -1 || (truck.truck?.vendor.name || "").toLowerCase().indexOf(search) > -1
                            })}
                            columns={columns.filter((col) => shownColumns.indexOf(col.key as TOrderContainerColumn) > -1)}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && trucks.length < 1 ? <Stack styles={{ root: { padding: props.variant === 'plain' ? 0 : 10 } }}>
                <Text>Truck(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <OrderTruckForm orderId={props.orderId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'details' && activeTruck ? <OrderTruckDetails orderId={props.orderId} truckId={activeTruck.id} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default OrderTrucksList;
